/*
 * Copyright (c) 2025 CalAmp Corp. All Rights Reserved
 */
import React, {
  useEffect,
  useRef,
  useState
} from 'react';
import {List} from '@material-ui/core';
import MenuItem from '@material-ui/core/MenuItem';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {ButtonSpinner} from '@ion/dls/src/components/loading/ButtonSpinner';
import Render from '@ion/dls/src/render/Render';
import {dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {RoleResult} from '@ion/query/src/hooks/useInfiniteRolesQuery';
import DynamicLookupOptionsLoader from '../../search/DynamicLookupOptionsLoader';
import DynamicLookupHierarchyTooltipOption from '../DynamicLookupHierarchyTooltipOption';

export interface RoleSearchDropdownProps {
  data: RoleResult[];
  isLoading: boolean;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  onSelection: Function;
  accountTree: any;
  isFetching: boolean;
}

const useStyles = makeStyles((theme: DLSTheme) => ({
  spinnerWrapper : {
    display        : 'flex',
    justifyContent : 'center'
  },
  noResults : {
    minHeight  : 24,
    display    : 'flex',
    alignItems : 'center',
    padding    : '8px 32px 0px',
    color      : dlsColor('grey3'),
    ...dlsTypography('body3')
  },
  suggestionItem : {
    height               : 40,
    padding              : '0 32px !important',
    fontSize             : 14,
    fontFamily           : theme.typography.fontFamily,
    '&:not(:last-child)' : {
      background : `linear-gradient(${theme.dls.colors.grey4}, ${theme.dls.colors.grey4}) bottom/ 95% 1px no-repeat`
    },
    '&:hover, &:focus' : {
      background : theme.dls.colors.grey5
    }
  },
  filesLookUpWrapper : {
    padding : 10
  },
  list : {
    maxHeight : '400px',
    overflow  : 'auto'
  },
  spinner : {
    display        : 'flex',
    alignContent   : 'center',
    justifyContent : 'center',
    padding        : '4px'
  }
}));

/**
 * @RoleSearchDropdown
 * Is a component that renders the dropdown for the role search.
 *
 * ```typescript
 * import {RoleSearchDropdown} from './RoleSearchDropdown';
 *
 * ...
 *
 * <RoleSearchDropdown data={data}
 *   searchString={searchString}
 *   isLoading={isLoading}
 *   fetchNextPage={fetchNextPage}
 *   hasNextPage={hasNextPage}
 *   onSelection={onSelection}
 *   accountTree={accountTree}
 *   isFetching={isFetching} />
 *
 * ```
 *
 * @param data          - The list of roles to display in the dropdown
 * @param searchString  - The search string to filter the roles
 * @param isLoading     - A boolean to indicate if the data is loading
 * @param fetchNextPage - A function to fetch the next page of data
 * @param hasNextPage   - A boolean to indicate if there is a next page of data
 * @param onSelection   - A function to handle the selection of a role
 * @param accountTree   - The account tree to display the hierarchy of the role
 * @param isFetching    - A boolean to indicate if the data is fetching
 *
 */
const RoleSearchDropdown = ({
  fetchNextPage,
  onSelection,
  accountTree,
  hasNextPage,
  isFetching,
  isLoading,
  data
}: RoleSearchDropdownProps) => {
  const classes                                   = useStyles();
  const observer                                  = useRef<IntersectionObserver | null>(null);
  const lastElementRef                            = useRef<HTMLLIElement | null>(null);
  const [showInitialLoader, setShowInitialLoader] = useState<boolean>(true);

  /**
   * This useEffect hook implements infinite scrolling by using an IntersectionObserver
   * to load additional content. It observes the visibility of the last element in a list
   * (lastElementRef.current).
   *
   * Steps:
   * 1. Checks if data is currently being fetched (`isFetching`). If true, it exits early
   *    to avoid duplicate calls.
   * 2. Disconnects any existing observer to prevent leaks.
   * 3. Defines a callback that triggers `fetchNextPage()` if the last element is visible
   *    and more pages are available.
   * 4. Sets up a new IntersectionObserver with this callback and a threshold of 0. (0
   *    meaning the callback is triggered when any part of the element is visible).
   * 5. Attaches the observer to `lastElementRef.current` if it exists.
   * 6. On cleanup, disconnects the observer to free resources.
   *
   * Dependencies:
   * - @fetchNextPage - Function to fetch the next page of data
   * - @hasNextPage   - Boolean to indicate if there is a next page of data
   * - @isFetching    - Boolean to indicate if the data is currently fetching the next page
   *
   * For more details on IntersectionObserver: https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
   */
  useEffect(() => {
    if (isFetching) {
      return;
    }
    if (observer.current) {
      observer.current.disconnect();
    }
    const callback = entries => {
      if (entries?.[0].isIntersecting && hasNextPage) {
        fetchNextPage();
      }
    };
    observer.current = new IntersectionObserver(callback, {threshold: 0});
    if (lastElementRef.current) {
      observer.current?.observe(lastElementRef.current);
    }
    return () => observer?.current.disconnect();
  }, [
    fetchNextPage,
    hasNextPage,
    isFetching
  ]);

  useEffect(() => {
    setShowInitialLoader(isLoading && !data?.length);
  }, [isLoading, data]);

  return (
    <List className={classes.list}>
      <Render.If condition={showInitialLoader}>
        <DynamicLookupOptionsLoader />
      </Render.If>

      {data?.map((role, index) => (
        <MenuItem key={role?.id}
          id={`${role.id}-${index}`}
          ref={index === data?.length - 1 && hasNextPage ? lastElementRef : null}
          classes={{root: classes.suggestionItem}}
          onClick={onSelection(role)}
          button>
          <DynamicLookupHierarchyTooltipOption accountTree={accountTree} item={role} />
        </MenuItem>
      ))}

      <Render.If condition={hasNextPage}>
        <div className={classes.spinner}>
          {isFetching && <ButtonSpinner size={24} />}
        </div>
      </Render.If>

      <Render.If condition={!isLoading && !hasNextPage && !data?.length}>
        <span className={classes.noResults}>
          {translate('common:modularsearch.noMatchingResults')}
        </span>
      </Render.If>
    </List>
  );
};

export default RoleSearchDropdown;
